import { FC, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import ErrorBoundaryMessage from '../../app/components/error-handling/ErrorBoundaryMessage';
import AppSpinner from '../../common/components/AppSpinner';
import EmptyPageAvatar from '../../common/components/EmptyPageAvatar';
import GeneralErrorPage from '../../common/components/GeneralErrorPage';
import SidebarLayout from '../../elements/SidebarLayout/SidebarLayout';
import { useGradualUpdate, useScan } from '../../new-scan/actions/scan-actions';
import {
  clearStore,
  loadExclusions,
  onSort,
  removeSnoozeIssue,
  updateExpiredAtById,
} from '../actions/exclusions-actions';
import {
  loadExclusionsFilters,
  setInitialFilters,
} from '../actions/exclusions-filters-actions';
import ExclusionsTable from '../components/Exclusions-table/ExclusionsTable';
import RemoveSnoozeModal from '../components/RemoveSnoozeModal';
import UpdateSnoozeExpiredAtModal from '../components/UpdateSnoozeExpiredAtModal';
import ExclusionModal from '../exclusionsOptions/ExclusionModal';
import {
  setExpireDate,
  setModalRemoveSnooze,
  setModalUpdateExpiredAt,
} from '../store-actions/exclusions-store-actions';
import exclusionsStore from '../stores/exclusions-store';
import ExclusionsEmptyPage from './ExclusionsEmptyPage';
import ExclusionsSideBarContainer from './ExclusionsSideBarContainer';

const ExclusionsContainer: FC<React.PropsWithChildren<unknown>> = () => {
  const {
    exclusions,
    generalError,
    loading,
    showModal: showRemoveExclusionDialog,
    selectedExclusion,
    isUpdateExpiredAtById,
    updateExpiredAtModal,
    removeSnoozeModal,
    removeSnoozeLoading,
    updateExpiredAtModalBanner,
  } = useSnapshot(exclusionsStore);

  const [searchParams] = useSearchParams();
  const { isCancelScanLoading } = useScan();
  const filters = searchParams.get('filters');

  const { inProgress } = useGradualUpdate(
    useCallback(() => {
      setInitialFilters(filters);
      loadExclusions({ update: true });
      loadExclusionsFilters();
    }, [filters]),
  );

  useEffect(() => {
    setInitialFilters(filters);
    loadExclusions({ update: true });
    loadExclusionsFilters();
  }, [filters]);

  useEffect(() => {
    return clearStore;
  }, []);

  const handleCloseUpdatExpireDate = () => {
    setModalUpdateExpiredAt(false);
  };

  const handleCloseRemoveSnooze = () => {
    setModalRemoveSnooze(false);
  };

  const handleUpdate = useCallback(() => {
    if (selectedExclusion) {
      updateExpiredAtById({
        exclusionId: selectedExclusion?.exclusionId,
        expiredAt: selectedExclusion?.expiredAt,
      });
    }
  }, [selectedExclusion]);

  const handleRemoveSnooze = useCallback(() => {
    if (selectedExclusion) {
      removeSnoozeIssue({
        exclusionId: selectedExclusion.exclusionId,
        expiredAt: null,
      });
    }
  }, [selectedExclusion]);

  if (generalError) {
    return (
      <GeneralErrorPage
        showOxIcon
        errorTitle='Oops, an error has occurred :('
        errorMsg={<ErrorBoundaryMessage />}
      />
    );
  }

  if (isCancelScanLoading) {
    return <AppSpinner text='Canceling scan. Please wait...' />;
  }

  if (inProgress && exclusions.length === 0) {
    return (
      <EmptyPageAvatar
        text={`A scan is now running. The first results will appear soon so don't MOOOOve.`}
      />
    );
  }

  if ((loading.isIdle() || loading.isPending()) && exclusions.length === 0) {
    return <AppSpinner text='Fetching Exclusions' />;
  }

  if (loading.isError()) {
    return (
      <EmptyPageAvatar text='No exclusions found. Exclusions done from the Issues screen after a scan will show here.' />
    );
  }

  return (
    <SidebarLayout aside={<ExclusionsSideBarContainer />}>
      {exclusions.length === 0 && <ExclusionsEmptyPage />}
      {exclusions.length > 0 && (
        <ExclusionsTable exclusions={exclusions} onSort={onSort} />
      )}
      <ExclusionModal
        open={showRemoveExclusionDialog}
        exclusion={selectedExclusion}
      />
      <UpdateSnoozeExpiredAtModal
        open={updateExpiredAtModal}
        issueName={selectedExclusion?.exclusionAppliedOn}
        appName={selectedExclusion?.appName}
        onCloseExcludeIssue={handleCloseUpdatExpireDate}
        handleUpdate={handleUpdate}
        isLoading={isUpdateExpiredAtById}
        handleSnoozeDateChange={setExpireDate}
        expiredAt={selectedExclusion?.expiredAt}
        isBanner={updateExpiredAtModalBanner}
      />
      <RemoveSnoozeModal
        open={removeSnoozeModal}
        onCloseExcludeIssue={handleCloseRemoveSnooze}
        handleRemove={handleRemoveSnooze}
        isLoading={removeSnoozeLoading}
        issueName={selectedExclusion?.exclusionAppliedOn}
      />
    </SidebarLayout>
  );
};

export default ExclusionsContainer;
